// src/components/whiteboard/EnhancedWhiteboardCanvas.js
import React, { useRef, useEffect, useState, useCallback } from 'react';
import { fabric } from 'fabric';
import styled from 'styled-components';
import { useClassroom } from '../../contexts/ClassroomContext';
import { supabase } from '../../services/supabase';
import { Calculator, Grid, TrendingUp, Settings, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Maximize2, Minimize2, ZoomIn, RotateCcw } from 'lucide-react';

const CanvasContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: ${props => props.backgroundColor || 'white'};
  overflow: hidden;
  cursor: ${props => props.cursor || 'default'};
  display: block;
  
  canvas {
    display: block;
    width: 100% !important;
    height: 100% !important;
    position: absolute;
    top: 0;
    left: 0;
    touch-action: none; /* Prevent default touch behaviors */
  }
`;

const NavigationControl = styled.button`
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  ${props => props.position === 'top' && `
    top: 20px;
    left: 50%;
    transform: translateX(-50%);
  `}
  
  ${props => props.position === 'bottom' && `
    bottom: 20px;
    left: 50%;
    transform: translateX(-50%);
  `}
  
  ${props => props.position === 'left' && `
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
  `}
  
  ${props => props.position === 'right' && `
    right: 20px;
    top: 50%;
    transform: translateY(-50%);
  `}
  
  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: ${props => {
      if (props.position === 'top' || props.position === 'bottom') return 'translateX(-50%) scale(1.1)';
      if (props.position === 'left' || props.position === 'right') return 'translateY(-50%) scale(1.1)';
      return 'scale(1.1)';
    }};
  }
  
  &:active {
    transform: ${props => {
      if (props.position === 'top' || props.position === 'bottom') return 'translateX(-50%) scale(0.95)';
      if (props.position === 'left' || props.position === 'right') return 'translateY(-50%) scale(0.95)';
      return 'scale(0.95)';
    }};
  }
`;

const VideoPanel = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: ${props => props.height}px;
  background: #1a1a1a;
  transform: translateY(${props => props.isVisible ? '0' : '-100%'});
  transition: transform 0.3s ease;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 18px;
`;

const MathToolbar = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  display: flex;
  gap: 8px;
  z-index: 1000;
  background: rgba(255, 255, 255, 0.9);
  padding: 8px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
`;

const ToolButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 6px;
  background: ${props => props.active ? '#3b82f6' : '#f3f4f6'};
  color: ${props => props.active ? 'white' : '#374151'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.active ? '#2563eb' : '#e5e7eb'};
  }
`;

const FunctionInput = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.95);
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-width: 300px;
  transition: all 0.3s ease;
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
`;

const InputField = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 8px;
  font-family: 'Courier New', monospace;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FunctionButton = styled.button`
  background: #10b981;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  margin-right: 8px;

  &:hover {
    background: #059669;
  }
`;

const SettingsButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 0, 0, 0.9);
    transform: scale(1.1);
  }
`;

const FullscreenIndicator = styled.div`
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  z-index: 1000;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: none;
`;

const FloatingControlPanel = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: ${props => props.active ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  position: relative;

  &:hover {
    background: ${props => props.active ? '#2563eb' : 'rgba(255, 255, 255, 0.2)'};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ControlTooltip = styled.div`
  position: absolute;
  right: 50px;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.9);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  z-index: 1001;

  ${ControlButton}:hover & {
    opacity: 1;
  }

  &::after {
    content: '';
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-left-color: rgba(0, 0, 0, 0.9);
  }
`;

const ScalingControls = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.8);
  border-radius: 12px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(10px);
  opacity: ${props => props.visible ? 1 : 0};
  pointer-events: ${props => props.visible ? 'auto' : 'none'};
  transition: opacity 0.3s ease;
`;

const ScaleButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: ${props => props.active ? '#3b82f6' : 'rgba(255, 255, 255, 0.1)'};
  color: white;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;
  font-weight: 600;

  &:hover {
    background: ${props => props.active ? '#2563eb' : 'rgba(255, 255, 255, 0.2)'};
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const ScaleIndicator = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  z-index: 1000;
  backdrop-filter: blur(10px);
`;

// App Launcher Components
const AppLauncherButton = styled.button`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-size: 24px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  z-index: 1000;
  transform: scale(var(--ui-scale));
  transform-origin: bottom right;

  &:hover {
    transform: scale(1.1) scale(var(--ui-scale));
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.4);
  }
`;

const AppLauncherPanel = styled.div`
  position: fixed;
  bottom: 90px;
  right: 20px;
  background: rgba(0, 0, 0, 0.9);
  border-radius: 12px;
  padding: 16px;
  display: ${props => props.visible ? 'grid' : 'none'};
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  z-index: 1001;
  transform: scale(var(--ui-scale));
  transform-origin: bottom right;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(10px);
`;

const AppButton = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 12px 8px;
  border: none;
  border-radius: 8px;
  background: ${props => props.color || '#374151'};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  min-height: 70px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  .app-icon {
    font-size: 24px;
    margin-bottom: 4px;
  }

  .app-name {
    font-size: 10px;
    font-weight: 500;
    text-align: center;
    line-height: 1.2;
  }
`;

// App Window Components
const AppWindow = styled.div`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  width: ${props => props.width}px;
  height: ${props => props.isMinimized ? '40px' : `${props.height}px`};
  background: white;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 1002;
  overflow: hidden;
  transition: all 0.3s ease;
  transform: scale(${props => props.scale});
  transform-origin: top left;
`;

const AppWindowHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  background: ${props => props.color || '#374151'};
  color: white;
  padding: 0 12px;
  cursor: move;
  user-select: none;
`;

const AppWindowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
`;

const AppWindowControls = styled.div`
  display: flex;
  gap: 4px;
`;

const AppWindowButton = styled.button`
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 3px;
  background: ${props => props.type === 'close' ? '#ef4444' : props.type === 'minimize' ? '#f59e0b' : '#10b981'};
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
    transform: scale(1.1);
  }
`;

const AppWindowContent = styled.div`
  width: 100%;
  height: calc(100% - 40px);
  overflow: hidden;
`;

const AppIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
`;

const AppResizeHandle = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: 20px;
  cursor: se-resize;
  background: linear-gradient(-45deg, transparent 30%, #666 30%, #666 70%, transparent 70%);
  opacity: 0.5;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
`;

const AppScaleControls = styled.div`
  position: absolute;
  top: -30px;
  right: 0;
  display: flex;
  gap: 4px;
  background: rgba(0, 0, 0, 0.8);
  padding: 4px;
  border-radius: 4px;
`;

const AppScaleButton = styled.button`
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 3px;
  background: #374151;
  color: white;
  cursor: pointer;
  font-size: 12px;
  font-weight: bold;
  transition: all 0.2s ease;

  &:hover {
    background: #4b5563;
  }

  &.active {
    background: #3b82f6;
  }
`;

const EnhancedWhiteboardCanvas = ({ 
  currentTool, 
  settings = {}, 
  onSettingsChange,
  isFullscreen = false,
  onToggleFullscreen,
  onOpenSettings,
  onToggleMaterialsPanel,
  onToggleRosterPanel,
  onToggleVideoPanel,
  onToggleSettingsPanel
}) => {
  const canvasRef = useRef(null);
  const fabricCanvasRef = useRef(null);
  const { whiteboardData, setWhiteboardData, roomId } = useClassroom();
  const [activeMathTool, setActiveMathTool] = useState('pen');
  const [functionInput, setFunctionInput] = useState('');
  const [showVideoPanel, setShowVideoPanel] = useState(false);
  const [videoPanelHeight, setVideoPanelHeight] = useState(200);
  const [panelsVisible, setPanelsVisible] = useState({
    top: true,
    left: true,
    right: true,
    bottom: true
  });
  const [showSettings, setShowSettings] = useState(false);
  const [showFullscreenIndicator, setShowFullscreenIndicator] = useState(false);
  const [uiScale, setUiScale] = useState(1);
  const [showScalingControls, setShowScalingControls] = useState(false);
  const [localCurrentTool, setLocalCurrentTool] = useState(currentTool || 'pen');
  const [openApps, setOpenApps] = useState({});
  const [showAppLauncher, setShowAppLauncher] = useState(false);

  // Subject apps configuration
  const subjectApps = {
    primaths: {
      name: 'PriMaths',
      url: 'https://primaths.appsokay.net',
      icon: '🔢',
      color: '#3b82f6'
    },
    mathsdaily: {
      name: 'MathsDaily',
      url: 'https://mathsdaily.appsokay.net',
      icon: '📊',
      color: '#10b981'
    },
    prienglish: {
      name: 'PriEnglish',
      url: 'https://prienglish.appsokay.net',
      icon: '📚',
      color: '#f59e0b'
    },
    englishdaily: {
      name: 'EnglishDaily',
      url: 'https://englishdaily.appsokay.net',
      icon: '📝',
      color: '#ef4444'
    },
    grammarspeak: {
      name: 'GrammarSpeak',
      url: 'https://grammarspeak.appsokay.net',
      icon: '🗣️',
      color: '#8b5cf6'
    },
    goessay: {
      name: 'GoEssay',
      url: 'https://goessay.appsokay.net',
      icon: '✍️',
      color: '#06b6d4'
    }
  };

  // App launcher functions
  const handleLaunchApp = useCallback((appId) => {
    const app = subjectApps[appId];
    if (!app) return;

    const newApp = {
      id: appId,
      name: app.name,
      url: app.url,
      icon: app.icon,
      color: app.color,
      x: 100 + Object.keys(openApps).length * 50,
      y: 100 + Object.keys(openApps).length * 50,
      width: 800,
      height: 600,
      scale: 1,
      isMinimized: false
    };

    setOpenApps(prev => ({
      ...prev,
      [appId]: newApp
    }));
    setShowAppLauncher(false);
  }, [openApps, subjectApps]);

  const handleCloseApp = useCallback((appId) => {
    setOpenApps(prev => {
      const newApps = { ...prev };
      delete newApps[appId];
      return newApps;
    });
  }, []);

  const handleMinimizeApp = useCallback((appId) => {
    setOpenApps(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        isMinimized: !prev[appId].isMinimized
      }
    }));
  }, []);

  const handleResizeApp = useCallback((appId, newWidth, newHeight) => {
    setOpenApps(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        width: newWidth,
        height: newHeight
      }
    }));
  }, []);

  const handleMoveApp = useCallback((appId, newX, newY) => {
    setOpenApps(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        x: newX,
        y: newY
      }
    }));
  }, []);

  const handleScaleApp = useCallback((appId, newScale) => {
    setOpenApps(prev => ({
      ...prev,
      [appId]: {
        ...prev[appId],
        scale: newScale
      }
    }));
  }, []);

  // Handle tool changes
  const handleToolChange = useCallback((tool) => {
    if (!fabricCanvasRef.current) return;
    
    const canvas = fabricCanvasRef.current;
    
    // Remove existing event listeners
    canvas.off('mouse:down');
    canvas.off('mouse:move');
    canvas.off('mouse:up');
    
    // Drawing state variables
    let isDrawingRect = false;
    let currentRect = null;
    let isDrawingCircle = false;
    let currentCircle = null;
    let isPlacingText = false;
    
    switch (tool) {
      case 'pen':
        canvas.isDrawingMode = true;
        canvas.freeDrawingBrush.color = settings.textColor || '#ffffff';
        canvas.freeDrawingBrush.width = settings.textSize ? Math.max(1, settings.textSize / 8) : 3;
        break;
      case 'eraser':
        canvas.isDrawingMode = true;
        canvas.freeDrawingBrush.color = settings.selectedBackground || '#000000';
        canvas.freeDrawingBrush.width = 10;
        break;
      case 'rectangle':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        
        // Add rectangle drawing functionality
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingRect) {
            const pointer = canvas.getPointer(opt.e);
            currentRect = new fabric.Rect({
              left: pointer.x,
              top: pointer.y,
              width: 0,
              height: 0,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentRect);
            canvas.setActiveObject(currentRect);
            isDrawingRect = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingRect && currentRect) {
            const pointer = canvas.getPointer(opt.e);
            currentRect.set({
              width: Math.abs(pointer.x - currentRect.left),
              height: Math.abs(pointer.y - currentRect.top)
            });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingRect && currentRect) {
            // Finish the current rectangle
            isDrawingRect = false;
            currentRect = null;
            // Switch back to pen tool after finishing rectangle
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'circle':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        
        // Add circle drawing functionality
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingCircle) {
            const pointer = canvas.getPointer(opt.e);
            currentCircle = new fabric.Circle({
              left: pointer.x,
              top: pointer.y,
              radius: 0,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentCircle);
            canvas.setActiveObject(currentCircle);
            isDrawingCircle = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingCircle && currentCircle) {
            const pointer = canvas.getPointer(opt.e);
            const radius = Math.sqrt(
              Math.pow(pointer.x - currentCircle.left, 2) + Math.pow(pointer.y - currentCircle.top, 2)
            );
            currentCircle.set({ radius });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingCircle && currentCircle) {
            // Finish the current circle
            isDrawingCircle = false;
            currentCircle = null;
            // Switch back to pen tool after finishing circle
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'text':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        
        // Add text functionality
        canvas.on('mouse:down', (opt) => {
          if (!isPlacingText) {
            const pointer = canvas.getPointer(opt.e);
            const text = new fabric.Text('Click to edit', {
              left: pointer.x,
              top: pointer.y,
              fontSize: settings.textSize || 16,
              fill: settings.textColor || '#ffffff',
              fontFamily: 'Arial',
              fontWeight: settings.textStyle?.bold ? 'bold' : 'normal',
              fontStyle: settings.textStyle?.italic ? 'italic' : 'normal',
              underline: settings.textStyle?.underline || false
            });
            canvas.add(text);
            canvas.setActiveObject(text);
            text.enterEditing();
            isPlacingText = true;
            
            // Switch back to pen tool after placing text
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'triangle':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        let isDrawingTriangle = false;
        let currentTriangle = null;
        
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingTriangle) {
            const pointer = canvas.getPointer(opt.e);
            currentTriangle = new fabric.Triangle({
              left: pointer.x,
              top: pointer.y,
              width: 0,
              height: 0,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentTriangle);
            canvas.setActiveObject(currentTriangle);
            isDrawingTriangle = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingTriangle && currentTriangle) {
            const pointer = canvas.getPointer(opt.e);
            currentTriangle.set({
              width: Math.abs(pointer.x - currentTriangle.left),
              height: Math.abs(pointer.y - currentTriangle.top)
            });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingTriangle && currentTriangle) {
            isDrawingTriangle = false;
            currentTriangle = null;
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'pyramid':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        let isDrawingPyramid = false;
        let currentPyramid = null;
        
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingPyramid) {
            const pointer = canvas.getPointer(opt.e);
            currentPyramid = new fabric.Polygon([
              { x: pointer.x, y: pointer.y + 40 },
              { x: pointer.x - 20, y: pointer.y },
              { x: pointer.x + 20, y: pointer.y },
              { x: pointer.x, y: pointer.y + 40 }
            ], {
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentPyramid);
            canvas.setActiveObject(currentPyramid);
            isDrawingPyramid = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingPyramid && currentPyramid) {
            const pointer = canvas.getPointer(opt.e);
            const width = Math.abs(pointer.x - currentPyramid.left);
            const height = Math.abs(pointer.y - currentPyramid.top);
            currentPyramid.set({
              points: [
                { x: 0, y: height },
                { x: -width/2, y: 0 },
                { x: width/2, y: 0 },
                { x: 0, y: height }
              ]
            });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingPyramid && currentPyramid) {
            isDrawingPyramid = false;
            currentPyramid = null;
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'cone':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        let isDrawingCone = false;
        let currentCone = null;
        
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingCone) {
            const pointer = canvas.getPointer(opt.e);
            currentCone = new fabric.Polygon([
              { x: 0, y: 0 },
              { x: -15, y: 30 },
              { x: 15, y: 30 }
            ], {
              left: pointer.x,
              top: pointer.y,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentCone);
            canvas.setActiveObject(currentCone);
            isDrawingCone = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingCone && currentCone) {
            const pointer = canvas.getPointer(opt.e);
            const width = Math.abs(pointer.x - currentCone.left);
            const height = Math.abs(pointer.y - currentCone.top);
            currentCone.set({
              points: [
                { x: 0, y: 0 },
                { x: -width/2, y: height },
                { x: width/2, y: height }
              ]
            });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingCone && currentCone) {
            isDrawingCone = false;
            currentCone = null;
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'sphere':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        let isDrawingSphere = false;
        let currentSphere = null;
        
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingSphere) {
            const pointer = canvas.getPointer(opt.e);
            currentSphere = new fabric.Circle({
              left: pointer.x,
              top: pointer.y,
              radius: 0,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentSphere);
            canvas.setActiveObject(currentSphere);
            isDrawingSphere = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingSphere && currentSphere) {
            const pointer = canvas.getPointer(opt.e);
            const radius = Math.sqrt(
              Math.pow(pointer.x - currentSphere.left, 2) + Math.pow(pointer.y - currentSphere.top, 2)
            );
            currentSphere.set({ radius });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingSphere && currentSphere) {
            isDrawingSphere = false;
            currentSphere = null;
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'hemisphere':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        let isDrawingHemisphere = false;
        let currentHemisphere = null;
        
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingHemisphere) {
            const pointer = canvas.getPointer(opt.e);
            currentHemisphere = new fabric.Circle({
              left: pointer.x,
              top: pointer.y,
              radius: 0,
              startAngle: 0,
              endAngle: Math.PI,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentHemisphere);
            canvas.setActiveObject(currentHemisphere);
            isDrawingHemisphere = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingHemisphere && currentHemisphere) {
            const pointer = canvas.getPointer(opt.e);
            const radius = Math.sqrt(
              Math.pow(pointer.x - currentHemisphere.left, 2) + Math.pow(pointer.y - currentHemisphere.top, 2)
            );
            currentHemisphere.set({ radius });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingHemisphere && currentHemisphere) {
            isDrawingHemisphere = false;
            currentHemisphere = null;
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'cylinder':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        let isDrawingCylinder = false;
        let currentCylinder = null;
        
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingCylinder) {
            const pointer = canvas.getPointer(opt.e);
            currentCylinder = new fabric.Rect({
              left: pointer.x,
              top: pointer.y,
              width: 0,
              height: 0,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentCylinder);
            canvas.setActiveObject(currentCylinder);
            isDrawingCylinder = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingCylinder && currentCylinder) {
            const pointer = canvas.getPointer(opt.e);
            currentCylinder.set({
              width: Math.abs(pointer.x - currentCylinder.left),
              height: Math.abs(pointer.y - currentCylinder.top)
            });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingCylinder && currentCylinder) {
            isDrawingCylinder = false;
            currentCylinder = null;
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'cube':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        let isDrawingCube = false;
        let currentCube = null;
        
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingCube) {
            const pointer = canvas.getPointer(opt.e);
            currentCube = new fabric.Polygon([
              { x: 0, y: 0 },
              { x: 30, y: 0 },
              { x: 40, y: 10 },
              { x: 10, y: 10 },
              { x: 0, y: 0 },
              { x: 0, y: 30 },
              { x: 10, y: 40 },
              { x: 10, y: 10 }
            ], {
              left: pointer.x,
              top: pointer.y,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentCube);
            canvas.setActiveObject(currentCube);
            isDrawingCube = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingCube && currentCube) {
            const pointer = canvas.getPointer(opt.e);
            const width = Math.abs(pointer.x - currentCube.left);
            const height = Math.abs(pointer.y - currentCube.top);
            const depth = Math.min(width, height) * 0.3; // 3D depth effect
            currentCube.set({
              points: [
                { x: 0, y: 0 },
                { x: width, y: 0 },
                { x: width + depth, y: depth },
                { x: depth, y: depth },
                { x: 0, y: 0 },
                { x: 0, y: height },
                { x: depth, y: height + depth },
                { x: depth, y: depth }
              ]
            });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingCube && currentCube) {
            isDrawingCube = false;
            currentCube = null;
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      case 'diamond':
        canvas.isDrawingMode = false;
        canvas.selection = true;
        let isDrawingDiamond = false;
        let currentDiamond = null;
        
        canvas.on('mouse:down', (opt) => {
          if (!isDrawingDiamond) {
            const pointer = canvas.getPointer(opt.e);
            currentDiamond = new fabric.Polygon([
              { x: 0, y: -20 },
              { x: 20, y: 0 },
              { x: 0, y: 20 },
              { x: -20, y: 0 }
            ], {
              left: pointer.x,
              top: pointer.y,
              fill: 'transparent',
              stroke: settings.textColor || '#ffffff',
              strokeWidth: 2
            });
            canvas.add(currentDiamond);
            canvas.setActiveObject(currentDiamond);
            isDrawingDiamond = true;
          }
        });
        
        canvas.on('mouse:move', (opt) => {
          if (isDrawingDiamond && currentDiamond) {
            const pointer = canvas.getPointer(opt.e);
            const width = Math.abs(pointer.x - currentDiamond.left);
            const height = Math.abs(pointer.y - currentDiamond.top);
            currentDiamond.set({
              points: [
                { x: 0, y: -height },
                { x: width, y: 0 },
                { x: 0, y: height },
                { x: -width, y: 0 }
              ]
            });
            canvas.renderAll();
          }
        });
        
        canvas.on('mouse:up', () => {
          if (isDrawingDiamond && currentDiamond) {
            isDrawingDiamond = false;
            currentDiamond = null;
            setTimeout(() => {
              setLocalCurrentTool('pen');
              handleToolChange('pen');
            }, 100);
          }
        });
        break;
      default:
        canvas.isDrawingMode = true;
        canvas.freeDrawingBrush.color = settings.textColor || '#ffffff';
        canvas.freeDrawingBrush.width = 3;
    }
  }, [settings.textColor, settings.textSize, settings.selectedBackground, settings.textStyle]);

  // Initialize canvas
  useEffect(() => {
    if (!canvasRef.current) return;

    const initCanvas = () => {
      // Get container dimensions
      const getCanvasDimensions = () => {
        const container = canvasRef.current.parentElement;
        if (container) {
          const rect = container.getBoundingClientRect();
          return {
            width: Math.floor(rect.width),
            height: Math.floor(rect.height)
          };
        }
        return {
          width: window.innerWidth,
          height: window.innerHeight
        };
      };

      const dimensions = getCanvasDimensions();
      
      // Create canvas with proper dimensions - always full size regardless of UI scale
      const canvas = new fabric.Canvas(canvasRef.current, {
        width: dimensions.width,
        height: dimensions.height,
        backgroundColor: settings.selectedBackground || '#000000',
        selection: false,
        preserveObjectStacking: true,
        renderOnAddRemove: true,
        skipTargetFind: true,
        enableRetinaScaling: false // Disable retina scaling for better performance
      });

      fabricCanvasRef.current = canvas;

      // Set up drawing tools
      canvas.isDrawingMode = true;
      canvas.freeDrawingBrush.width = 3;
      canvas.freeDrawingBrush.color = settings.textColor || '#ffffff';

      // Update drawing brush color when settings change
      const updateBrushColor = () => {
        if (canvas.freeDrawingBrush) {
          canvas.freeDrawingBrush.color = settings.textColor || '#ffffff';
          canvas.freeDrawingBrush.width = settings.textSize ? Math.max(1, settings.textSize / 8) : 3;
        }
      };

      updateBrushColor();

      // Handle resize events
      const handleResize = () => {
        const newDimensions = getCanvasDimensions();
        if (newDimensions.width > 0 && newDimensions.height > 0) {
          canvas.setDimensions(newDimensions, { backstoreOnly: false });
          canvas.renderAll();
        }
      };

      // Use ResizeObserver for accurate container tracking
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          const { width, height } = entry.contentRect;
          if (width > 0 && height > 0) {
            canvas.setDimensions({ width: Math.floor(width), height: Math.floor(height) }, { backstoreOnly: false });
            canvas.renderAll();
          }
        }
      });

      // Observe the canvas container
      if (canvasRef.current.parentElement) {
        resizeObserver.observe(canvasRef.current.parentElement);
      }

      // Also listen to window resize as backup
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        resizeObserver.disconnect();
        canvas.dispose();
      };
    };

    // Initialize with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(initCanvas, 50);
    
    return () => {
      clearTimeout(timeoutId);
    };
  }, [panelsVisible, settings.selectedBackground, settings.textColor, settings.textSize]);

  // Handle tool changes
  useEffect(() => {
    if (currentTool && fabricCanvasRef.current) {
      setLocalCurrentTool(currentTool);
      handleToolChange(currentTool);
    }
  }, [currentTool, handleToolChange]);

  // Initialize with pen tool
  useEffect(() => {
    if (fabricCanvasRef.current && !currentTool) {
      handleToolChange('pen');
    }
  }, [handleToolChange, currentTool]);

  // Handle double-click for video panel (kept for compatibility)
  const handleDoubleClick = useCallback((e) => {
    if (e.target === canvasRef.current && e.clientY < 100) {
      setShowVideoPanel(!showVideoPanel);
    }
  }, [showVideoPanel]);


  // Panel control handlers with correct mapping
  const handlePanelControl = useCallback((position) => {
    switch (position) {
      case 'left':
        if (onToggleMaterialsPanel) onToggleMaterialsPanel();
        break;
      case 'top':
        if (onToggleVideoPanel) onToggleVideoPanel();
        break;
      case 'right':
        if (onToggleRosterPanel) onToggleRosterPanel();
        break;
      case 'bottom':
        if (onToggleSettingsPanel) onToggleSettingsPanel();
        break;
      default:
        break;
    }
  }, [onToggleMaterialsPanel, onToggleVideoPanel, onToggleRosterPanel, onToggleSettingsPanel]);

  // Scaling functions
  const handleScaleChange = useCallback((scale) => {
    setUiScale(scale);
    
    // Apply scaling to UI elements only, not the canvas
    const uiElements = document.querySelectorAll('.scalable-ui, .scalable-panel, .scalable-button, .scalable-text, .scalable-icon, .scalable-tooltip, .scalable-nav-control, .scalable-settings, .scalable-color-grid, .scalable-color-option, .scalable-input, .scalable-btn, .scalable-mode-selector, .scalable-mode-button, .scalable-roster, .scalable-video, .scalable-math-toolbar, .scalable-function-input, .scalable-scaling-controls, .scalable-scale-button, .scalable-indicator');
    uiElements.forEach(element => {
      element.style.transform = `scale(${scale})`;
      element.style.transformOrigin = 'top left';
    });

    // Handle canvas scaling - corrected complementary logic
    if (fabricCanvasRef.current) {
      const canvas = fabricCanvasRef.current;
      
      // Always get the base container dimensions first
      const container = canvasRef.current.parentElement;
      if (!container) return;
      
      const baseRect = container.getBoundingClientRect();
      const baseWidth = Math.floor(baseRect.width);
      const baseHeight = Math.floor(baseRect.height);
      
      if (scale !== 1) {
        if (scale < 1) {
          // For fractional scales (x1/2, x1/4, etc.), shrink content and extend canvas
          const contentScale = scale; // x1/2 = 0.5 (content becomes smaller)
          
          // Get current canvas dimensions
          const currentWidth = canvas.getWidth();
          const currentHeight = canvas.getHeight();
          
          // Calculate new canvas dimensions by extending from current size
          const canvasSizeMultiplier = 1 / scale; // x1/2 = 2 (canvas becomes larger)
          const newWidth = currentWidth * canvasSizeMultiplier;
          const newHeight = currentHeight * canvasSizeMultiplier;
          
          // Set new canvas dimensions (extend the canvas)
          canvas.setDimensions({ width: newWidth, height: newHeight });
          
          // Scale down the content to make drawings smaller
          canvas.setZoom(contentScale);
          
          // Set viewport transform
          canvas.viewportTransform = [contentScale, 0, 0, contentScale, 0, 0];
          canvas.renderAll();
        } else if (scale > 1) {
          // For enlargement scales (x2, x4, etc.), keep canvas at current size but zoom content
          // Don't change canvas dimensions - just zoom the content
          canvas.setZoom(scale);
          canvas.viewportTransform = [scale, 0, 0, scale, 0, 0];
          canvas.renderAll();
        }
      } else {
        // Reset to normal (x1) - use base container dimensions
        canvas.setDimensions({ width: baseWidth, height: baseHeight });
        canvas.setZoom(1);
        canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
        canvas.renderAll();
      }
    }
  }, []);

  const resetScale = useCallback(() => {
    // Force reset UI scale to 1
    setUiScale(1);
    
    // Reset ALL UI elements to original scale - comprehensive recovery
    const uiElements = document.querySelectorAll('.scalable-ui, .scalable-panel, .scalable-button, .scalable-text, .scalable-icon, .scalable-tooltip, .scalable-nav-control, .scalable-settings, .scalable-color-grid, .scalable-color-option, .scalable-input, .scalable-btn, .scalable-mode-selector, .scalable-mode-button, .scalable-roster, .scalable-video, .scalable-math-toolbar, .scalable-function-input, .scalable-scaling-controls, .scalable-scale-button, .scalable-indicator');
    uiElements.forEach(element => {
      element.style.transform = 'scale(1)';
      element.style.transformOrigin = 'top left';
    });

    // Complete canvas reset - recovery from any logic error
    if (fabricCanvasRef.current) {
      const canvas = fabricCanvasRef.current;
      const container = canvasRef.current.parentElement;
      
      if (container) {
        // Get fresh container dimensions
        const rect = container.getBoundingClientRect();
        const baseWidth = Math.floor(rect.width);
        const baseHeight = Math.floor(rect.height);
        
        // Store current background color before any changes
        const currentBackground = canvas.backgroundColor;
        
        // Complete reset - force canvas back to original state
        try {
          // Reset dimensions to base container size
          canvas.setDimensions({ 
            width: baseWidth, 
            height: baseHeight 
          }, { backstoreOnly: false });
          
          // Reset zoom to 1
          canvas.setZoom(1);
          
          // Reset viewport transform to identity
          canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
          
          // Restore background color if it was set
          if (currentBackground) {
            canvas.setBackgroundColor(currentBackground, () => {
              canvas.renderAll();
            });
          } else {
            // Set default background if none was set
            canvas.setBackgroundColor(settings.selectedBackground || '#000000', () => {
              canvas.renderAll();
            });
          }
          
          // Force a complete re-render
          canvas.renderAll();
          
        } catch (error) {
          console.warn('Canvas reset error, attempting recovery:', error);
          // If there's an error, try a more aggressive reset
          canvas.clear();
          canvas.setDimensions({ width: baseWidth, height: baseHeight });
          canvas.setZoom(1);
          canvas.viewportTransform = [1, 0, 0, 1, 0, 0];
          canvas.setBackgroundColor(settings.selectedBackground || '#000000');
          canvas.renderAll();
        }
      }
    }
  }, [settings.selectedBackground]);

  // Text styling functions
  const addStyledText = useCallback((text, options = {}) => {
    if (!fabricCanvasRef.current) return;

    const textObject = new fabric.Text(text, {
      left: 100,
      top: 100,
      fontSize: settings.textSize || 16,
      fill: settings.textColor || '#000000',
      fontFamily: 'Arial',
      fontWeight: settings.textStyle?.bold ? 'bold' : 'normal',
      fontStyle: settings.textStyle?.italic ? 'italic' : 'normal',
      underline: settings.textStyle?.underline || false,
      ...options
    });

    fabricCanvasRef.current.add(textObject);
    fabricCanvasRef.current.renderAll();
  }, [settings]);

  // Function plotting
  const plotFunction = useCallback((funcString) => {
    if (!fabricCanvasRef.current) return;

    try {
      const canvas = fabricCanvasRef.current;
      const width = canvas.width;
      const height = canvas.height;
      const centerX = width / 2;
      const centerY = height / 2;
      const scale = 50;

      const points = [];
      for (let x = -10; x <= 10; x += 0.1) {
        try {
          let y;
          if (funcString.includes('x^2') || funcString.includes('x²')) {
            y = x * x;
          } else if (funcString.includes('sin')) {
            y = Math.sin(x);
          } else if (funcString.includes('cos')) {
            y = Math.cos(x);
          } else if (funcString.includes('x^3') || funcString.includes('x³')) {
            y = x * x * x;
          } else if (funcString.includes('sqrt') || funcString.includes('√')) {
            y = Math.sqrt(Math.abs(x));
          } else {
            y = x;
          }

          const canvasX = centerX + (x * scale);
          const canvasY = centerY - (y * scale);

          if (canvasX >= 0 && canvasX <= width && canvasY >= 0 && canvasY <= height) {
            points.push({ x: canvasX, y: canvasY });
          }
        } catch (e) {
          console.warn('Error evaluating function at x =', x, e);
        }
      }

      if (points.length > 1) {
        const pathString = points.map((point, index) => 
          `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
        ).join(' ');

        const path = new fabric.Path(pathString, {
          stroke: '#ef4444',
          strokeWidth: 2,
          fill: '',
          selectable: true,
          evented: true
        });

        canvas.add(path);
        canvas.renderAll();
      }
    } catch (error) {
      console.error('Error plotting function:', error);
    }
  }, []);

  // Handle function input
  const handlePlotFunction = useCallback(() => {
    if (functionInput.trim()) {
      plotFunction(functionInput);
      setFunctionInput('');
    }
  }, [functionInput, plotFunction]);

  // Fullscreen toggle
  const toggleFullscreen = useCallback(() => {
    if (onToggleFullscreen) {
      onToggleFullscreen();
    }
    setShowFullscreenIndicator(true);
    setTimeout(() => setShowFullscreenIndicator(false), 2000);
  }, [onToggleFullscreen]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'f':
            e.preventDefault();
            toggleFullscreen();
            break;
          case 's':
            e.preventDefault();
            setShowSettings(!showSettings);
            break;
          case 'Enter':
            if (activeMathTool === 'function') {
              handlePlotFunction();
            }
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [toggleFullscreen, showSettings, activeMathTool, handlePlotFunction]);

  return (
    <CanvasContainer 
      backgroundColor={settings.selectedBackground}
      onDoubleClick={handleDoubleClick}
    >
      <canvas ref={canvasRef} />
      
      {/* Video Panel */}
      <VideoPanel 
        height={videoPanelHeight}
        isVisible={showVideoPanel}
      >
        📹 视频面板 - 双击顶部区域或使用顶部箭头控制显示
      </VideoPanel>

      {/* Navigation Controls with Correct Panel Mapping */}
      <NavigationControl 
        position="top" 
        onClick={() => handlePanelControl('top')}
        title="切换视频面板 (Video Panel)"
      >
        <ChevronUp size={20} />
      </NavigationControl>
      
      <NavigationControl 
        position="bottom" 
        onClick={() => handlePanelControl('bottom')}
        title="切换设置面板 (Settings Panel)"
      >
        <ChevronDown size={20} />
      </NavigationControl>
      
      <NavigationControl 
        position="left" 
        onClick={() => handlePanelControl('left')}
        title="切换素材库面板 (Materials Panel)"
      >
        <ChevronLeft size={20} />
      </NavigationControl>
      
      <NavigationControl 
        position="right" 
        onClick={() => handlePanelControl('right')}
        title="切换花名册面板 (Roster Panel)"
      >
        <ChevronRight size={20} />
      </NavigationControl>

      {/* Drawing Tools Toolbar */}
      <MathToolbar visible={panelsVisible.top}>
        <ToolButton 
          active={localCurrentTool === 'pen'} 
          onClick={() => {
            setLocalCurrentTool('pen');
            handleToolChange('pen');
          }}
          title="Pen Tool"
        >
          ✏️
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'eraser'} 
          onClick={() => {
            setLocalCurrentTool('eraser');
            handleToolChange('eraser');
          }}
          title="Eraser Tool"
        >
          🧹
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'rectangle'} 
          onClick={() => {
            setLocalCurrentTool('rectangle');
            handleToolChange('rectangle');
          }}
          title="Rectangle Tool"
        >
          ⬜
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'circle'} 
          onClick={() => {
            setLocalCurrentTool('circle');
            handleToolChange('circle');
          }}
          title="Circle Tool"
        >
          ⭕
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'triangle'} 
          onClick={() => {
            setLocalCurrentTool('triangle');
            handleToolChange('triangle');
          }}
          title="Triangle Tool"
        >
          🔺
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'pyramid'} 
          onClick={() => {
            setLocalCurrentTool('pyramid');
            handleToolChange('pyramid');
          }}
          title="Pyramid Tool"
        >
          🔺
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'cone'} 
          onClick={() => {
            setLocalCurrentTool('cone');
            handleToolChange('cone');
          }}
          title="Cone Tool"
        >
          🍦
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'sphere'} 
          onClick={() => {
            setLocalCurrentTool('sphere');
            handleToolChange('sphere');
          }}
          title="Sphere Tool"
        >
          ⚪
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'hemisphere'} 
          onClick={() => {
            setLocalCurrentTool('hemisphere');
            handleToolChange('hemisphere');
          }}
          title="Hemisphere Tool"
        >
          🌙
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'cylinder'} 
          onClick={() => {
            setLocalCurrentTool('cylinder');
            handleToolChange('cylinder');
          }}
          title="Cylinder Tool"
        >
          🥫
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'cube'} 
          onClick={() => {
            setLocalCurrentTool('cube');
            handleToolChange('cube');
          }}
          title="Cube Tool"
        >
          🧊
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'diamond'} 
          onClick={() => {
            setLocalCurrentTool('diamond');
            handleToolChange('diamond');
          }}
          title="Diamond Tool"
        >
          💎
        </ToolButton>
        <ToolButton 
          active={localCurrentTool === 'text'} 
          onClick={() => {
            setLocalCurrentTool('text');
            handleToolChange('text');
          }}
          title="Text Tool"
        >
          T
        </ToolButton>
      </MathToolbar>

      {/* Function Input Panel */}
      <FunctionInput visible={activeMathTool === 'function'}>
        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#374151' }}>
          AI函数图像 (AI Function Graphing)
        </h4>
        <InputField
          type="text"
          placeholder="Enter function (e.g., x^2, sin(x), sqrt(x))"
          value={functionInput}
          onChange={(e) => setFunctionInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handlePlotFunction()}
        />
        <FunctionButton onClick={handlePlotFunction}>
          Plot Function
        </FunctionButton>
        <FunctionButton 
          onClick={() => setFunctionInput('')}
          style={{ background: '#6b7280' }}
        >
          Clear
        </FunctionButton>
      </FunctionInput>

      {/* Floating Control Panel */}
      <FloatingControlPanel>
        <ControlButton 
          onClick={() => onOpenSettings && onOpenSettings()}
          title="设置"
        >
          <Settings size={20} />
          <ControlTooltip>设置 (Settings)</ControlTooltip>
        </ControlButton>
        
        <ControlButton 
          onClick={toggleFullscreen}
          active={isFullscreen}
          title="全屏"
        >
          {isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
          <ControlTooltip>
            {isFullscreen ? '退出全屏' : '进入全屏'} (Fullscreen)
          </ControlTooltip>
        </ControlButton>
        
        <ControlButton 
          onClick={() => setShowVideoPanel(!showVideoPanel)}
          active={showVideoPanel}
          title="视频面板"
        >
          📹
          <ControlTooltip>视频面板 (Video Panel)</ControlTooltip>
        </ControlButton>
        
        <ControlButton 
          onClick={() => setShowScalingControls(!showScalingControls)}
          active={showScalingControls}
          title="缩放控制"
        >
          <ZoomIn size={20} />
          <ControlTooltip>缩放控制 (Scaling Controls)</ControlTooltip>
        </ControlButton>
      </FloatingControlPanel>

      {/* Scaling Controls */}
        <ScalingControls visible={showScalingControls}>
          {/* Zoom In (Shrinkage) Buttons */}
          <ScaleButton 
            onClick={() => handleScaleChange(0.5)}
            active={uiScale === 0.5}
            style={{ background: '#ef4444' }}
          >
            x0.5
          </ScaleButton>
          <ScaleButton 
            onClick={() => handleScaleChange(0.3)}
            active={uiScale === 0.3}
            style={{ background: '#ef4444' }}
          >
            x0.3
          </ScaleButton>
          <ScaleButton 
            onClick={() => handleScaleChange(0.1)}
            active={uiScale === 0.1}
            style={{ background: '#ef4444' }}
          >
            x0.1
          </ScaleButton>
          <ScaleButton 
            onClick={() => handleScaleChange(0.05)}
            active={uiScale === 0.05}
            style={{ background: '#ef4444' }}
          >
            x0.05
          </ScaleButton>
          <ScaleButton 
            onClick={() => handleScaleChange(0.01)}
            active={uiScale === 0.01}
            style={{ background: '#ef4444' }}
          >
            x0.01
          </ScaleButton>
        
        {/* Reset Button */}
        <ScaleButton 
          onClick={resetScale}
          active={uiScale === 1}
          style={{ background: '#10b981' }}
        >
          <RotateCcw size={16} />
        </ScaleButton>
        
        {/* Zoom Out (Enlarge) Buttons */}
        <ScaleButton 
          onClick={() => handleScaleChange(2)}
          active={uiScale === 2}
        >
          x2
        </ScaleButton>
        <ScaleButton 
          onClick={() => handleScaleChange(4)}
          active={uiScale === 4}
        >
          x4
        </ScaleButton>
        <ScaleButton 
          onClick={() => handleScaleChange(6)}
          active={uiScale === 6}
        >
          x6
        </ScaleButton>
        <ScaleButton 
          onClick={() => handleScaleChange(8)}
          active={uiScale === 8}
        >
          x8
        </ScaleButton>
        <ScaleButton 
          onClick={() => handleScaleChange(10)}
          active={uiScale === 10}
        >
          x10
        </ScaleButton>
      </ScalingControls>

        {/* Scale Indicator */}
        <ScaleIndicator>
          缩放: {uiScale < 1 ? 
            (uiScale === 0.5 ? '0.5' : 
             uiScale === 0.3 ? '0.3' : 
             uiScale === 0.1 ? '0.1' : 
             uiScale === 0.05 ? '0.05' : 
             uiScale === 0.01 ? '0.01' : 
             `${uiScale}`) : 
            `${uiScale}`}x
        </ScaleIndicator>

      {/* Fullscreen Indicator */}
      <FullscreenIndicator visible={showFullscreenIndicator}>
        {isFullscreen ? '退出全屏 (Ctrl+F)' : '进入全屏 (Ctrl+F)'}
      </FullscreenIndicator>

      {/* App Launcher */}
      <AppLauncherButton onClick={() => setShowAppLauncher(!showAppLauncher)}>
        📱
      </AppLauncherButton>

      <AppLauncherPanel visible={showAppLauncher}>
        {Object.entries(subjectApps).map(([appId, app]) => (
          <AppButton
            key={appId}
            color={app.color}
            onClick={() => handleLaunchApp(appId)}
          >
            <div className="app-icon">{app.icon}</div>
            <div className="app-name">{app.name}</div>
          </AppButton>
        ))}
      </AppLauncherPanel>

      {/* App Windows */}
      {Object.values(openApps).map((app) => (
        <AppWindow
          key={app.id}
          x={app.x}
          y={app.y}
          width={app.width}
          height={app.height}
          scale={app.scale}
          isMinimized={app.isMinimized}
        >
          <AppWindowHeader color={app.color}>
            <AppWindowTitle>
              <span>{app.icon}</span>
              <span>{app.name}</span>
            </AppWindowTitle>
            <AppWindowControls>
              <AppWindowButton
                type="minimize"
                onClick={() => handleMinimizeApp(app.id)}
                title="最小化"
              >
                {app.isMinimized ? '□' : '−'}
              </AppWindowButton>
              <AppWindowButton
                type="close"
                onClick={() => handleCloseApp(app.id)}
                title="关闭"
              >
                ×
              </AppWindowButton>
            </AppWindowControls>
          </AppWindowHeader>
          
          {!app.isMinimized && (
            <>
              <AppWindowContent>
                <AppIframe
                  src={app.url}
                  title={app.name}
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                />
              </AppWindowContent>
              
              <AppScaleControls>
                <AppScaleButton
                  className={app.scale === 0.5 ? 'active' : ''}
                  onClick={() => handleScaleApp(app.id, 0.5)}
                >
                  0.5
                </AppScaleButton>
                <AppScaleButton
                  className={app.scale === 0.75 ? 'active' : ''}
                  onClick={() => handleScaleApp(app.id, 0.75)}
                >
                  0.75
                </AppScaleButton>
                <AppScaleButton
                  className={app.scale === 1 ? 'active' : ''}
                  onClick={() => handleScaleApp(app.id, 1)}
                >
                  1
                </AppScaleButton>
                <AppScaleButton
                  className={app.scale === 1.25 ? 'active' : ''}
                  onClick={() => handleScaleApp(app.id, 1.25)}
                >
                  1.25
                </AppScaleButton>
                <AppScaleButton
                  className={app.scale === 1.5 ? 'active' : ''}
                  onClick={() => handleScaleApp(app.id, 1.5)}
                >
                  1.5
                </AppScaleButton>
              </AppScaleControls>
              
              <AppResizeHandle
                onMouseDown={(e) => {
                  e.preventDefault();
                  const startX = e.clientX;
                  const startY = e.clientY;
                  const startWidth = app.width;
                  const startHeight = app.height;

                  const handleMouseMove = (e) => {
                    const newWidth = Math.max(400, startWidth + (e.clientX - startX));
                    const newHeight = Math.max(300, startHeight + (e.clientY - startY));
                    handleResizeApp(app.id, newWidth, newHeight);
                  };

                  const handleMouseUp = () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                  };

                  document.addEventListener('mousemove', handleMouseMove);
                  document.addEventListener('mouseup', handleMouseUp);
                }}
              />
            </>
          )}
        </AppWindow>
      ))}
    </CanvasContainer>
  );
};

export default EnhancedWhiteboardCanvas;
